import { toConsumableArray as _toConsumableArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import { abbr } from '@zillow/formatters';
import LineChart from '../PaymentDonutChart/LineChart/LineChart.js';
import PropInterpolatorWrapper from '../PaymentDonutChart/Chart/common/PropInterpolator.js';
import { getWholeDollars } from '../../utils/zhl-calculator.js';
import withDimensions from '../PaymentDonutChart/withDimensions.js';
import { useLastValidPayemntValues, safe } from './VALoanCalculator.utils.js';

var GRAPH_HEIGHT = 275;
var legendIndexFormatter = function legendIndexFormatter(x) {
  return x === null ? x : x + " month" + (x !== 1 ? 's' : '');
};
var xAxisFormatter = safe(function (x) {
  return x + ' mo';
});
var yAxisFormatter = safe(function (x) {
  return '$' + abbr(x);
});
var getAccessibleValue = function getAccessibleValue(index, message) {
  return index % 12 === 0 ? message : '';
};
var VALoanCalculatorBase = function VALoanCalculatorBase(_ref) {
  var containerWidth = _ref.containerWidth;
  var theme = useTheme();
  var _theme$constellation$ = theme.constellation.colors,
    backgroundDarkGreen = _theme$constellation$.backgroundDarkGreen,
    backgroundDarkAqua = _theme$constellation$.backgroundDarkAqua,
    backgroundDarkPurple = _theme$constellation$.backgroundDarkPurple,
    borderDark = _theme$constellation$.borderDark,
    textDark = _theme$constellation$.textDark;
  // Line chart color scheme
  var _colors = [backgroundDarkGreen, backgroundDarkAqua, backgroundDarkPurple];
  var form = useFormContext();
  var lastValidValues = useLastValidPayemntValues(form);
  var chartDataProps = lastValidValues.calculations.amortization;
  var lineData = useMemo(function () {
    var _chartDataProps$reduc = chartDataProps.reduce(function (acc, _ref2, i) {
        var totalPrincipal = _ref2.totalPrincipal,
          totalInterest = _ref2.totalInterest,
          remainingBalance = _ref2.remainingBalance;
        return {
          principal: [].concat(_toConsumableArray(acc.principal), [{
            x: i,
            y: totalPrincipal,
            accessibleValue: getAccessibleValue(i, 'Principal paid')
          }]),
          interest: [].concat(_toConsumableArray(acc.interest), [{
            x: i,
            y: totalInterest,
            accessibleValue: getAccessibleValue(i, 'Interest paid')
          }]),
          remaining: [].concat(_toConsumableArray(acc.remaining), [{
            x: i,
            y: remainingBalance,
            accessibleValue: getAccessibleValue(i, 'Remaining balance')
          }])
        };
      }, {
        principal: [],
        interest: [],
        remaining: []
      }),
      principal = _chartDataProps$reduc.principal,
      interest = _chartDataProps$reduc.interest,
      remaining = _chartDataProps$reduc.remaining;
    return [{
      name: 'Principal',
      formatter: function formatter(value) {
        return (
          // eslint-disable-next-line no-restricted-globals
          isNaN(value) || value === null ? null : getWholeDollars(value)
        );
      },
      values: principal
    }, {
      name: 'Interest',
      formatter: function formatter(value) {
        return (
          // eslint-disable-next-line no-restricted-globals
          isNaN(value) || value === null ? null : getWholeDollars(value)
        );
      },
      values: interest
    }, {
      name: 'Remaining',
      formatter: function formatter(value) {
        return (
          // eslint-disable-next-line no-restricted-globals
          isNaN(value) || value === null ? null : getWholeDollars(value)
        );
      },
      values: remaining
    }];
  }, [chartDataProps]);
  return /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'relative',
      paddingTop: '120px'
    }
  }, /*#__PURE__*/React.createElement(LineChart, {
    containerWidth: containerWidth,
    legend: false,
    legendIndexFormatter: legendIndexFormatter,
    margins: {
      top: 10,
      right: 50,
      bottom: 40,
      left: 20
    },
    data: lineData,
    colors: function colors(i) {
      return _colors[i];
    },
    xHideOrigin: true,
    xAxisTickCount: 3,
    xAxisFormatter: xAxisFormatter,
    yAxisTickCount: 4,
    yAxisFormatter: yAxisFormatter,
    lineWidth: 2,
    axesColor: borderDark,
    tickTextStroke: textDark,
    height: GRAPH_HEIGHT,
    displayDataPoints: false,
    "aria-label": "Mortgage payoff schedule chart",
    xAxisLabel: "Months",
    yAxisLabel: "Paid amount",
    "aria-hidden": "true",
    fontFamily: theme.constellation.fonts.sansSerif
  }));
};
VALoanCalculatorBase.propTypes = process.env.NODE_ENV !== "production" ? {
  containerWidth: PropTypes.number.isRequired
} : {};
var VALoanCalculatorLineChart = PropInterpolatorWrapper(withDimensions(VALoanCalculatorBase, {
  defaultWidth: 550,
  handleHeight: false
}));
var VALoanCalculatorLineChart$1 = VALoanCalculatorLineChart;

export { VALoanCalculatorLineChart$1 as default };
